import React, { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ------------------------------------------------------------------ */
/*  Brand tokens — shared with JobFeed / Navbar / Footer              */
/* ------------------------------------------------------------------ */
const INDIGO = '#5B47E0';
const EMERALD = '#10B981';

/* ------------------------------------------------------------------ */
/*  Icons (inline, stroke-based — same family as JobFeed)             */
/* ------------------------------------------------------------------ */
const Ic = ({ d, size = 16, className = '', fill = 'none', strokeWidth = 1.7, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {d ? <path d={d} /> : children}
  </svg>
);

const PinIcon = (p) => <Ic {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="2.6" /></Ic>;
const BagIcon = (p) => <Ic {...p}><rect x="3" y="7" width="18" height="13" rx="2.5" /><path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7" /><path d="M3 12h18" /></Ic>;
const ArrowIcon = (p) => <Ic {...p}><path d="M5 12h14m0 0-6-6m6 6-6 6" /></Ic>;
const BuildIcon = (p) => <Ic {...p}><path d="M4 21V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V21" /><path d="M14 9h4.5A1.5 1.5 0 0 1 20 10.5V21" /><path d="M2.5 21h19M7 8h3M7 12h3M7 16h3M17 13h0M17 17h0" /></Ic>;
const CodeIcon = (p) => <Ic {...p}><path d="m8 8-4 4 4 4M16 8l4 4-4 4M13 5l-2 14" /></Ic>;
const ChartIcon = (p) => <Ic {...p}><path d="M4 4v16h16" /><path d="M8 16l3-4 3 3 4-6" /></Ic>;
const MegaphoneIcon = (p) => <Ic {...p}><path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1Z" /><path d="M14 8a4 4 0 0 1 0 8M10 10v8" /></Ic>;
const PenIcon = (p) => <Ic {...p}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Ic>;
const HeartIcon = (p) => <Ic {...p}><path d="M12 20s-7-4.5-9.2-8.5A5 5 0 0 1 12 6a5 5 0 0 1 9.2 5.5C19 15.5 12 20 12 20Z" /></Ic>;
const HeadsetIcon = (p) => <Ic {...p}><path d="M4 13v-1a8 8 0 0 1 16 0v1" /><path d="M4 13h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm14 0h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1Z" /><path d="M20 17v1a3 3 0 0 1-3 3h-3" /></Ic>;

/* ------------------------------------------------------------------ */
/*  Popular categories — keyword maps to JobFeed search filter        */
/* ------------------------------------------------------------------ */
const CATEGORIES = [
  { icon: CodeIcon, label: 'Engineering', keyword: 'developer', tint: 'indigo' },
  { icon: ChartIcon, label: 'Data & Analytics', keyword: 'data', tint: 'emerald' },
  { icon: MegaphoneIcon, label: 'Marketing', keyword: 'marketing', tint: 'coral' },
  { icon: PenIcon, label: 'Design', keyword: 'design', tint: 'amber' },
  { icon: HeartIcon, label: 'Healthcare', keyword: 'health', tint: 'rose' },
  { icon: HeadsetIcon, label: 'Customer Support', keyword: 'support', tint: 'sky' },
];

const TINTS = {
  indigo: 'bg-indigo-50 text-indigo-600 ring-indigo-100 group-hover:bg-indigo-100',
  emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100 group-hover:bg-emerald-100',
  coral: 'bg-[#FFF1EE] text-[#FF6B57] ring-[#FFE2DC] group-hover:bg-[#FFE2DC]',
  amber: 'bg-amber-50 text-amber-600 ring-amber-100 group-hover:bg-amber-100',
  rose: 'bg-rose-50 text-rose-600 ring-rose-100 group-hover:bg-rose-100',
  sky: 'bg-sky-50 text-sky-600 ring-sky-100 group-hover:bg-sky-100',
};

/* Stable, pleasant gradient per company name (monogram bg) */
const GRADIENTS = [
  ['#5B47E0', '#7C5CE0'],
  ['#FF6B57', '#FF9C8E'],
  ['#10B981', '#34D399'],
  ['#0EA5E9', '#38BDF8'],
  ['#F59E0B', '#FBBF24'],
  ['#EC4899', '#F472B6'],
];
const gradientFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % GRADIENTS.length;
  return GRADIENTS[h];
};
const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '★';

/* ================================================================== */
/*  Section                                                            */
/* ================================================================== */
const TopCompanies = ({ jobs = [], onSelectCompany, onSelectKeyword }) => {
  const sectionRef = useRef(null);

  /* Derive "top companies hiring" straight from the live job feed */
  const companies = useMemo(() => {
    const map = new Map();
    jobs.forEach((job) => {
      const name = job.company_name || 'Unknown Company';
      if (!map.has(name)) {
        map.set(name, {
          name,
          logo: job.company_logo || null,
          openings: 0,
          locations: new Set(),
          tags: new Set(),
          remote: false,
        });
      }
      const c = map.get(name);
      c.openings += 1;
      if (!c.logo && job.company_logo) c.logo = job.company_logo;
      if (job.location) c.locations.add(job.location);
      if (job.is_remote) c.remote = true;
      (job.tags || []).forEach((t) => c.tags.add(t));
    });
    return Array.from(map.values())
      .map((c) => ({
        ...c,
        locations: Array.from(c.locations),
        tags: Array.from(c.tags).slice(0, 3),
      }))
      .sort((a, b) => b.openings - a.openings)
      .slice(0, 12);
  }, [jobs]);

  const totalOpen = useMemo(() => companies.reduce((s, c) => s + c.openings, 0), [companies]);

  /* GSAP scroll reveals */
  useEffect(() => {
    if (!sectionRef.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      gsap.from('.tc-head', {
        y: 18,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out',
        stagger: 0.08,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 82%' },
      });

      const rows = gsap.utils.toArray('.tc-row');
      if (rows.length) {
        gsap.set(rows, { opacity: 0, x: -18 });
        ScrollTrigger.batch(rows, {
          start: 'top 94%',
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, {
              opacity: 1,
              x: 0,
              duration: 0.6,
              stagger: 0.08,
              ease: 'power3.out',
              clearProps: 'transform',
              overwrite: true,
            }),
        });
      }

      const pills = gsap.utils.toArray('.tc-pill');
      if (pills.length) {
        gsap.set(pills, { opacity: 0, y: 14 });
        ScrollTrigger.batch(pills, {
          start: 'top 96%',
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              duration: 0.5,
              stagger: 0.05,
              ease: 'power3.out',
              clearProps: 'transform',
              overwrite: true,
            }),
        });
      }

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, [companies.length]);

  if (!companies.length) return null;

  return (
    <section ref={sectionRef} className="mx-auto max-w-6xl px-4 pt-9 sm:px-6 lg:px-8">
      <style>{`
        .tc-row { position: relative; transition: transform .45s cubic-bezier(.22,1,.36,1); }
        .tc-row:hover { transform: translateX(7px); }
        .tc-rail { background: #E3E6EF; transform: scaleY(.35); transform-origin: center; transition: transform .45s cubic-bezier(.22,1,.36,1), background .45s; }
        .tc-row:hover .tc-rail { transform: scaleY(1); background: linear-gradient(180deg, ${INDIGO}, ${EMERALD}); }
        .tc-sweep { background: linear-gradient(90deg, rgba(91,71,224,.10), rgba(91,71,224,.03) 45%, rgba(91,71,224,0) 72%); opacity: 0; transition: opacity .45s ease; }
        .tc-row:hover .tc-sweep { opacity: 1; }
        .tc-mono { transition: transform .45s cubic-bezier(.22,1,.36,1); }
        .tc-row:hover .tc-mono { transform: scale(1.07) rotate(-3deg); }
        .tc-rank { transition: color .4s ease, transform .45s cubic-bezier(.22,1,.36,1); }
        .tc-row:hover .tc-rank { color: #C7D2FE; transform: translateX(-2px); }
        .tc-name { transition: color .3s ease; }
        .tc-row:hover .tc-name { color: ${INDIGO}; }
        .tc-go { transition: background-color .35s ease, color .35s ease, border-color .35s ease, transform .45s cubic-bezier(.22,1,.36,1); }
        .tc-row:hover .tc-go { background: ${INDIGO}; border-color: ${INDIGO}; color: #fff; transform: rotate(-45deg); }
        .tc-pill { transition: transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease; }
        .tc-pill:hover { transform: translateY(-3px); box-shadow: 0 14px 28px -16px rgba(91,71,224,.45); }
        @media (prefers-reduced-motion: reduce) {
          .tc-row:hover, .tc-row:hover .tc-mono, .tc-row:hover .tc-rank, .tc-row:hover .tc-go, .tc-pill:hover { transform: none; }
        }
      `}</style>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="tc-head flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-indigo-500">
            <BuildIcon size={14} /> Featured employers
          </div>
          <h2 className="tc-head mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[28px]">
            Top companies hiring now
          </h2>
          <p className="tc-head mt-1.5 text-[13px] text-slate-500">
            Ranked by open roles — tap any employer to jump straight to their listings.
          </p>
        </div>

        <div className="tc-head flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 ring-1 ring-slate-200">
          <BagIcon size={15} className="text-[#5B47E0]" />
          <span className="tabular-nums">{totalOpen}</span>
          <span className="text-slate-400">open roles across</span>
          <span className="tabular-nums">{companies.length}</span>
          <span className="text-slate-400">{companies.length === 1 ? 'company' : 'companies'}</span>
        </div>
      </div>

      {/* Ranked index — column header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-3 pb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        <span>Rank · Employer</span>
        <span>Open roles</span>
      </div>

      {/* Ranked index — rows (no boxes, just an editorial list) */}
      <div className="divide-y divide-slate-100">
        {companies.map((c, i) => {
          const [g1, g2] = gradientFor(c.name);
          return (
            <button
              key={c.name}
              type="button"
              onClick={() => onSelectCompany?.(c.name)}
              className="tc-row group flex w-full items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-3 text-left sm:gap-5 sm:px-3 sm:py-4"
            >
              <span className="tc-rail absolute inset-y-3 left-0 w-[3px] rounded-full" />
              <div className="tc-sweep pointer-events-none absolute inset-0" />

              {/* rank */}
              <span className="tc-rank relative w-7 shrink-0 text-right text-base font-bold tabular-nums text-slate-300 sm:w-9 sm:text-xl">
                {String(i + 1).padStart(2, '0')}
              </span>

              {/* monogram */}
              <div className="relative shrink-0">
                {c.logo ? (
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="tc-mono h-10 w-10 rounded-2xl object-cover ring-1 ring-slate-200 sm:h-12 sm:w-12"
                  />
                ) : (
                  <span
                    className="tc-mono grid h-10 w-10 place-items-center rounded-2xl text-[13px] font-bold text-white shadow-sm sm:h-12 sm:w-12 sm:text-[15px]"
                    style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}
                  >
                    {initialsOf(c.name)}
                  </span>
                )}
              </div>

              {/* name + meta */}
              <div className="relative min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="tc-name truncate text-[15px] font-semibold text-slate-900 sm:text-base">
                    {c.name}
                  </h3>
                  {c.remote && (
                    <span className="hidden shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-600 ring-1 ring-emerald-100 sm:inline">
                      Remote
                    </span>
                  )}
                </div>
                <div className="mt-1 flex min-w-0 items-center gap-2 text-[12px] text-slate-500">
                  <span className="inline-flex shrink-0 items-center gap-1">
                    <PinIcon size={13} className="text-slate-400" />
                    {c.locations.length ? c.locations.slice(0, 2).join(' · ') : 'Multiple locations'}
                  </span>
                  {!!c.tags.length && (
                    <>
                      <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                      <span className="truncate text-slate-400">{c.tags.join(' · ')}</span>
                    </>
                  )}
                </div>
              </div>

              {/* openings + go */}
              <div className="relative flex shrink-0 items-center gap-3 sm:gap-5">
                <div className="text-right">
                  <div className="text-[17px] font-bold tabular-nums text-slate-900">{c.openings}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                    {c.openings === 1 ? 'role' : 'roles'}
                  </div>
                </div>
                <span className="tc-go grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500">
                  <ArrowIcon size={16} />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Browse by category — pills, not tiles */}
      <div className="mt-10">
        <div className="tc-head flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-indigo-500">
          Explore roles
        </div>
        <h3 className="tc-head mt-2 text-lg font-semibold tracking-tight text-slate-900">Browse by category</h3>

        <div className="mt-4 flex flex-wrap gap-2.5">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              type="button"
              onClick={() => onSelectKeyword?.(cat.keyword)}
              className="tc-pill group inline-flex items-center gap-2.5 rounded-full bg-white py-1.5 pl-1.5 pr-4 ring-1 ring-slate-200/80"
            >
              <span className={`grid h-9 w-9 place-items-center rounded-full ring-1 transition-colors ${TINTS[cat.tint]}`}>
                <cat.icon size={17} />
              </span>
              <span className="text-[13px] font-semibold text-slate-700 transition-colors group-hover:text-slate-900">
                {cat.label}
              </span>
              <ArrowIcon
                size={15}
                className="-ml-0.5 text-slate-300 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-[#5B47E0]"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopCompanies;